import Twemoji from "react-twemoji";
import { ScopeType } from "sockapi/scope-types";
import { SockAPIScopeState } from "sockapi/scope-state";
import { SolveAttemptInfo } from "sockapi/solve-attempts";
import {
  useTeamAnswerableScopeState,
  usePuzzleDisplayName,
} from "stores/ScopeStateStore";
import { PuzzleLink } from "components/Link";
import { Spinner } from "components/Loading";
import { ShortDate } from "components/dates";
import "./PreviousGuessesDisplay.css";

export function SolveAttemptMessage(props: {
  teamId: string;
  info: Omit<SolveAttemptInfo, "timestamp">;
}) {
  const { teamId, info } = props;
  const { message, solveHintPrizePuzName } = info;
  const solveHintPrizeDisplayName = usePuzzleDisplayName(
    solveHintPrizePuzName ?? null,
    teamId
  );

  if (solveHintPrizePuzName !== undefined) {
    if (solveHintPrizeDisplayName === null) return <Spinner inline={true} />;
    return (
      <div>
        You solved the puzzle! Enjoy a free hint for{" "}
        <PuzzleLink puzName={solveHintPrizePuzName} subpage="hints">
          {solveHintPrizeDisplayName}
        </PuzzleLink>
        .
      </div>
    );
  }

  if (message !== undefined)
    return (
      <div>
        {message
          .trim()
          .split("\n")
          .map((line, i) => (
            <Twemoji key={i}>{line}</Twemoji>
          ))}
      </div>
    );
  return null;
}

export const PreviousGuessesDisplayPreloaded = (props: {
  teamId: string;
  teamAnswerableScopeState: SockAPIScopeState<ScopeType.TEAM_ANSWERABLE>;
  showKeroFeedbackForm?: boolean;
}) => {
  const {
    teamId,
    teamAnswerableScopeState: { submissions },
    showKeroFeedbackForm = false,
  } = props;

  // If there are no previous guesses, then hide the interface entirely.
  if (Object.keys(submissions).length === 0) return null;

  return (
    <>
      <h4>Previous guesses</h4>
      <table className="list-table previous-guess-table">
        <tbody>
          {Object.values(submissions)
            .sort((info1, info2) => info2.timestamp - info1.timestamp)
            .map((info) => (
              <tr key={info.timestamp}>
                <td>{info.answer}</td>
                <td>
                  <div className="previous-guess-answer-cell">
                    <Twemoji tag="span">
                      {(info.isCorrect ?? false)
                        ? "✅"
                        : (info.isWrong ?? false)
                          ? "❌"
                          : "💬"}
                    </Twemoji>
                    <SolveAttemptMessage info={info} teamId={teamId} />
                  </div>
                </td>
                <td>
                  <ShortDate timestamp={info.timestamp} />
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {showKeroFeedbackForm ? (
        <div style={{ textAlign: "center" }}>
          <a
            target="_blank"
            href="https://docs.google.com/forms/d/e/1FAIpQLSdNuU93XizFObLSEqg3P1JkcZVZBkymo3rABI8bkLlBJL_yAg/viewform"
            rel="noreferrer"
          >
            Please rate my first puzzle hunt~!
          </a>{" "}
          <span style={{ whiteSpace: "nowrap" }}>★~(◠‿◕✿)</span>
        </div>
      ) : null}
    </>
  );
};

function PreviousGuessesDisplay(props: { teamId: string; puzName: string }) {
  const { teamId, puzName } = props;
  const teamAnswerableScopeState = useTeamAnswerableScopeState(teamId, puzName);

  if (teamAnswerableScopeState === null) return <Spinner inline={true} />;

  return (
    <PreviousGuessesDisplayPreloaded
      teamId={teamId}
      teamAnswerableScopeState={teamAnswerableScopeState}
    />
  );
}

export default PreviousGuessesDisplay;
